import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { sb } from '../../lib/supabase';
import { useSupabaseAuth, useAccess } from '../../context/AuthContext';
import Loader from '../../components/Loader';
import {
  ROUTING_ARTEFACT_TYPES,
  ROUTING_BUILD_ITEM_TYPES,
  ROUTING_TEST_TYPES,
  ROUTING_TASK_TYPES,
  routingMismatch,
  mismatchHint,
} from '../../lib/functionalRoles';

const EMPTY_FORM = { name: '', full_name: '', is_delegating: false };

/** Unit-test Work and defect Assignment rows resolve via build item owner, not role lookup (§22.4.9). */
function resolvesFromBuildItem(rule) {
  if (rule.functional_role_id) return false;
  if (rule.source_artefact_type === 'Test script' && rule.test_type === 'unit') return true;
  if (rule.source_artefact_type === 'Defect' && rule.task_type_to_create === 'assignment') return true;
  return false;
}

export default function RoleTypes() {
  const { profile } = useSupabaseAuth();
  const { isAdmin } = useAccess();
  const orgId = profile?.organisation_id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [rules, setRules] = useState([]);
  const [assignmentCounts, setAssignmentCounts] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [rolesRes, rulesRes, assignRes] = await Promise.all([
        sb.from('functional_roles').select('*').eq('organisation_id', orgId).order('order_index'),
        sb.from('artefact_role_routing').select('*').eq('organisation_id', orgId).order('order_index'),
        sb.from('role_assignments').select('functional_role_id').eq('is_active', true),
      ]);
      if (rolesRes.error) throw rolesRes.error;
      if (rulesRes.error) throw rulesRes.error;
      if (assignRes.error) throw assignRes.error;

      const counts = {};
      for (const a of assignRes.data || []) {
        counts[a.functional_role_id] = (counts[a.functional_role_id] || 0) + 1;
      }

      setRoles(rolesRes.data || []);
      setRules(rulesRes.data || []);
      setAssignmentCounts(counts);
    } catch (e) {
      console.error('[RoleTypes] load failed', e);
      showToast('Failed to load role types');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const rolesById = useMemo(() => new Map(roles.map((r) => [r.id, r])), [roles]);

  const groupedRules = useMemo(() => {
    const groups = {};
    for (const r of rules) {
      const key = r.source_artefact_type;
      if (!groups[key]) groups[key] = [];
      groups[key].push(r);
    }
    return groups;
  }, [rules]);

  async function persistOrder(ordered) {
    setSaving(true);
    try {
      await Promise.all(ordered.map((r, i) =>
        sb.from('functional_roles').update({ order_index: i }).eq('id', r.id)
      ));
      setRoles(ordered.map((r, i) => ({ ...r, order_index: i })));
    } catch (e) {
      console.error('[RoleTypes] reorder failed', e);
      showToast('Failed to save order');
      load();
    } finally {
      setSaving(false);
    }
  }

  function onDrop(targetId) {
    if (!dragId || dragId === targetId) return;
    const ordered = [...roles].sort((a, b) => a.order_index - b.order_index);
    const fromIdx = ordered.findIndex((r) => r.id === dragId);
    const toIdx = ordered.findIndex((r) => r.id === targetId);
    const [moved] = ordered.splice(fromIdx, 1);
    ordered.splice(toIdx, 0, moved);
    setDragId(null);
    setDragOverId(null);
    persistOrder(ordered);
  }

  function startEdit(role) {
    setEditingId(role.id);
    setConfirmDeleteId(null);
    setEditForm({
      name: role.name,
      full_name: role.full_name,
      is_delegating: role.is_delegating,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
  }

  async function saveEdit(id) {
    const name = editForm.name.trim();
    const fullName = editForm.full_name.trim();
    if (!name || !fullName) {
      showToast('Name and full name are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { name, full_name: fullName, is_delegating: editForm.is_delegating };
      const { error } = await sb.from('functional_roles').update(payload).eq('id', id);
      if (error) throw error;
      setRoles((prev) => prev.map((r) => (r.id === id ? { ...r, ...payload } : r)));
      setEditingId(null);
      showToast('Role type saved', 'success');
    } catch (e) {
      console.error('[RoleTypes] save failed', e);
      showToast('Failed to save role type');
    } finally {
      setSaving(false);
    }
  }

  async function toggleGrid(id, show) {
    const { error } = await sb.from('functional_roles').update({ show_in_grid: show }).eq('id', id);
    if (error) {
      showToast('Failed to update grid visibility');
      return;
    }
    setRoles((prev) => prev.map((r) => (r.id === id ? { ...r, show_in_grid: show } : r)));
  }

  function requestDelete(id) {
    const count = assignmentCounts[id] || 0;
    if (count > 0) {
      setConfirmDeleteId(id);
      setEditingId(null);
    } else {
      deleteRole(id);
    }
  }

  async function deleteRole(id) {
    setSaving(true);
    try {
      const { error } = await sb.from('functional_roles').delete().eq('id', id);
      if (error) throw error;
      setRoles((prev) => prev.filter((r) => r.id !== id));
      setRules((prev) => prev.filter((r) => r.functional_role_id !== id));
      setConfirmDeleteId(null);
      showToast('Role type deleted', 'success');
    } catch (e) {
      console.error('[RoleTypes] delete failed', e);
      showToast('Failed to delete role type');
    } finally {
      setSaving(false);
    }
  }

  async function addRole() {
    const maxOrder = roles.reduce((m, r) => Math.max(m, r.order_index), -1);
    setSaving(true);
    try {
      const { data, error } = await sb.from('functional_roles').insert({
        organisation_id: orgId,
        name: 'New role',
        full_name: 'New role type',
        is_delegating: false,
        show_in_grid: false,
        order_index: maxOrder + 1,
        is_platform: false,
      }).select().single();
      if (error) throw error;
      setRoles((prev) => [...prev, data]);
      startEdit(data);
    } catch (e) {
      console.error('[RoleTypes] add failed', e);
      showToast('Failed to add role type');
    } finally {
      setSaving(false);
    }
  }

  async function updateRule(id, field, value) {
    const rule = rules.find((r) => r.id === id);
    if (!rule) return;
    const payload = { [field]: value === '' ? null : value };
    const { error } = await sb.from('artefact_role_routing').update(payload).eq('id', id);
    if (error) {
      showToast('Failed to update routing rule');
      return;
    }
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...payload } : r)));
  }

  async function deleteRule(id) {
    const { error } = await sb.from('artefact_role_routing').delete().eq('id', id);
    if (error) {
      showToast('Failed to delete rule');
      return;
    }
    setRules((prev) => prev.filter((r) => r.id !== id));
  }

  async function addRule() {
    const defaultRole = roles[0];
    if (!defaultRole) return;
    const maxOrder = rules.reduce((m, r) => Math.max(m, r.order_index), 0);
    setSaving(true);
    try {
      const { data, error } = await sb.from('artefact_role_routing').insert({
        organisation_id: orgId,
        source_artefact_type: 'Requirement',
        build_item_type: null,
        test_type: null,
        functional_role_id: defaultRole.id,
        task_type_to_create: 'approval',
        order_index: maxOrder + 1,
      }).select().single();
      if (error) throw error;
      setRules((prev) => [...prev, data]);
    } catch (e) {
      console.error('[RoleTypes] add rule failed', e);
      showToast('Failed to add routing rule');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Loader text="Loading role types…" />;

  if (!isAdmin) {
    return (
      <div className="content">
        <div className="empty" style={{ marginTop: 40 }}>Company admin access required.</div>
      </div>
    );
  }

  const sortedRoles = [...roles].sort((a, b) => a.order_index - b.order_index);

  return (
    <div className="rt-shell">
      <div className="rt-page-head">
        <div className="rt-page-head-top">
          <div>
            <div className="rt-page-eyebrow">Admin · Roles</div>
            <div className="rt-page-title">Role Types</div>
          </div>
          <button type="button" className="btn primary" disabled={saving} onClick={addRole}>+ Add role type</button>
        </div>
        <div className="rt-page-sub">
          The role dictionary used both to label what people do and to route tasks automatically as artefacts move through the lifecycle.
          Used by <strong>Role Assignments</strong> (assigning people to roles per process node) and by <strong>Task Routing</strong> below (which role gets notified when).
        </div>
      </div>

      <div className="rt-body-wrap">
        <div className="rt-section">
          <div className="rt-section-hdr">
            <div className="rt-section-title-block">
              <div className="rt-section-title">Role types</div>
              <div className="rt-section-desc">
                Drag to reorder. Delegating roles resolve to a lead who assigns a team member; non-delegating roles are the final named assignee.
                Show in grid controls whether the role appears as a column on the Role Assignments grid.
              </div>
            </div>
          </div>

          <div className="rt-list">
            {sortedRoles.map((rt) => {
              const count = assignmentCounts[rt.id] || 0;
              const isEditing = editingId === rt.id;
              const isConfirming = confirmDeleteId === rt.id;
              return (
                <div key={rt.id}>
                  <div
                    className={`rt-row${dragId === rt.id ? ' dragging' : ''}${dragOverId === rt.id ? ' drag-over' : ''}`}
                    draggable
                    onDragStart={() => setDragId(rt.id)}
                    onDragOver={(e) => { e.preventDefault(); if (rt.id !== dragId) setDragOverId(rt.id); }}
                    onDrop={(e) => { e.preventDefault(); onDrop(rt.id); }}
                    onDragEnd={() => { setDragId(null); setDragOverId(null); }}
                  >
                    <span className="rt-drag-handle" title="Drag to reorder">⠿⠿</span>
                    <div className="rt-name-block">
                      <div className="rt-name-line">
                        <span className="rt-name">{rt.full_name}</span>
                        {rt.is_platform
                          ? <span className="rt-tag rt-tag-platform">Platform</span>
                          : <span className="rt-tag rt-tag-custom">Custom</span>}
                        {rt.is_delegating && <span className="rt-tag rt-tag-delegating">Delegating</span>}
                      </div>
                      <div className="rt-fullname">
                        Short label: {rt.name}
                        {count > 0
                          ? ` · ${count} active assignment${count === 1 ? '' : 's'}`
                          : ' · no active assignments'}
                      </div>
                    </div>
                    <div className="rt-toggle-col">
                      <span className="rt-toggle-label">Show in grid</span>
                      <label className="rt-switch">
                        <input
                          type="checkbox"
                          checked={rt.show_in_grid}
                          onChange={(e) => toggleGrid(rt.id, e.target.checked)}
                        />
                        <span className="rt-switch-track" />
                        <span className="rt-switch-thumb" />
                      </label>
                    </div>
                    <div className="rt-actions">
                      <button type="button" className="btn sm icon ghost" title="Edit" onClick={() => startEdit(rt)}>✎</button>
                      <button type="button" className="btn sm icon ghost" title="Delete" onClick={() => requestDelete(rt.id)}>🗑</button>
                    </div>
                  </div>

                  {isConfirming && (
                    <div className="rt-delete-warning">
                      <span>
                        ⚠ &quot;{rt.full_name}&quot; has {count} active assignment{count === 1 ? '' : 's'} across programmes.
                        Reassign or remove {count === 1 ? 'it' : 'them'} first, or delete anyway.
                      </span>
                      <span className="rt-delete-actions">
                        <button type="button" className="btn sm" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                        <button type="button" className="btn sm danger" disabled={saving} onClick={() => deleteRole(rt.id)}>Delete anyway</button>
                      </span>
                    </div>
                  )}

                  {isEditing && (
                    <div className="rt-edit-form">
                      <div className="rt-form-grid">
                        <div className="rt-form-field">
                          <label>Full name</label>
                          <input
                            type="text"
                            value={editForm.full_name}
                            onChange={(e) => setEditForm((f) => ({ ...f, full_name: e.target.value }))}
                          />
                        </div>
                        <div className="rt-form-field">
                          <label>Short label (grid column)</label>
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="rt-form-toggle-row">
                        <label className="rt-switch" style={{ marginTop: 2 }}>
                          <input
                            type="checkbox"
                            checked={editForm.is_delegating}
                            onChange={(e) => setEditForm((f) => ({ ...f, is_delegating: e.target.checked }))}
                          />
                          <span className="rt-switch-track" />
                          <span className="rt-switch-thumb" />
                        </label>
                        <div className="rt-form-toggle-copy">
                          <div className="rt-form-toggle-title">Delegating role</div>
                          <div className="rt-form-toggle-help">
                            On: routes to a lead who picks a team member (creates an Assignment task first).
                            Off: routes straight to the named person (creates Approval, Work, or Review directly).
                          </div>
                        </div>
                      </div>
                      <div className="rt-form-actions">
                        <button type="button" className="btn sm" onClick={cancelEdit}>Cancel</button>
                        <button type="button" className="btn sm primary" disabled={saving} onClick={() => saveEdit(rt.id)}>Save changes</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button type="button" className="rt-add-row" disabled={saving} onClick={addRole}>+ Add role type</button>
        </div>

        <div className="rt-section">
          <div className="rt-section-hdr">
            <div className="rt-section-title-block">
              <div className="rt-section-title">Task routing rules</div>
              <div className="rt-section-desc">
                Maps a triggering artefact event to a role type and the kind of task it creates.
                Platform-seeded with sensible defaults; company admins can add org-specific rules or override the routed role type.
              </div>
            </div>
            <button type="button" className="btn sm" disabled={saving} onClick={addRule}>+ Add rule</button>
          </div>
          <div className="rt-routing-scroll">
            <table className="rt-routing">
              <thead>
                <tr>
                  <th>Source artefact</th>
                  <th>Filter</th>
                  <th>Routes to role</th>
                  <th>Creates task</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {Object.keys(groupedRules).map((groupType) => (
                  <Fragment key={groupType}>
                    <tr className="rt-group-hdr">
                      <td colSpan={5}>{groupType}</td>
                    </tr>
                    {groupedRules[groupType].map((rule) => {
                      const specialResolver = resolvesFromBuildItem(rule);
                      const role = rule.functional_role_id ? rolesById.get(rule.functional_role_id) : null;
                      const mismatch = !specialResolver && routingMismatch(role, rule.task_type_to_create);
                      const hint = !specialResolver ? mismatchHint(role, rule.task_type_to_create) : null;
                      const isBuildItem = rule.source_artefact_type === 'Build item';
                      const isTestScript = rule.source_artefact_type === 'Test script';
                      return (
                        <tr key={rule.id}>
                          <td>
                            <select
                              className="rt-rr-select"
                              value={rule.source_artefact_type}
                              onChange={(e) => updateRule(rule.id, 'source_artefact_type', e.target.value)}
                            >
                              {ROUTING_ARTEFACT_TYPES.map((t) => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            {isBuildItem ? (
                              <select
                                className="rt-rr-select"
                                value={rule.build_item_type || ''}
                                onChange={(e) => updateRule(rule.id, 'build_item_type', e.target.value || null)}
                              >
                                <option value="">— any —</option>
                                {ROUTING_BUILD_ITEM_TYPES.map((bt) => (
                                  <option key={bt} value={bt}>{bt}</option>
                                ))}
                              </select>
                            ) : isTestScript ? (
                              <select
                                className="rt-rr-select"
                                value={rule.test_type || ''}
                                onChange={(e) => updateRule(rule.id, 'test_type', e.target.value || null)}
                              >
                                <option value="">— any —</option>
                                {ROUTING_TEST_TYPES.map((tt) => (
                                  <option key={tt} value={tt}>{tt}</option>
                                ))}
                              </select>
                            ) : (
                              <span className="rt-build-type-chip">— any —</span>
                            )}
                          </td>
                          <td>
                            {specialResolver ? (
                              <span className="rt-resolver-chip" title="Resolved from the build item's Work-task assignee, not role_assignments">
                                Resolves from build item
                              </span>
                            ) : (
                              <select
                                className="rt-rr-select"
                                value={rule.functional_role_id || ''}
                                onChange={(e) => updateRule(rule.id, 'functional_role_id', e.target.value || null)}
                              >
                                {sortedRoles.map((r) => (
                                  <option key={r.id} value={r.id}>{r.full_name}</option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td>
                            <select
                              className="rt-rr-select"
                              value={rule.task_type_to_create}
                              onChange={(e) => updateRule(rule.id, 'task_type_to_create', e.target.value)}
                            >
                              {ROUTING_TASK_TYPES.map((t) => (
                                <option key={t.key} value={t.key}>{t.label}</option>
                              ))}
                            </select>
                            {mismatch && (
                              <span className="rt-rr-mismatch" title={hint}>⚠</span>
                            )}
                          </td>
                          <td>
                            <button type="button" className="rt-rr-del" title="Remove rule" onClick={() => deleteRule(rule.id)}>✕</button>
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
                {rules.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: '#888780', padding: 20 }}>
                      No routing rules yet. Add one or re-run the platform seed migration.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {toast && (
        <div className={`toast${toast.type === 'success' ? ' success' : ''}`} style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 300 }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
